import { type FC, useMemo } from 'react'
import { useMatch } from 'react-router'
import { useAppSelector } from '#app/store'
import { selectOrderedConfigurations } from '#app/store/selectors/orderedTruckConfigurations'
import type { TruckConfiguration } from '#app/store/slices/configurationSlice'
import { HeaderTabsContainer as SharedHeaderTabsContainer } from '#features/AppLayout'
import { HeaderTabs } from '#features/AppLayout/Header/HeaderTabs'
import { isLocalConfigurationId, isTmsConfigurationId } from '#pages/Configuration/components/useActiveConfiguration'
import { ConfigurationHeaderDropdownLink } from './Header/ConfigurationHeaderDropdownLink'
import { ConfigurationHeaderTab } from './Header/ConfigurationHeaderTab'
import { ManCodeHeader } from './Header/ManCodeHeader'

export const HeaderTabsContainer: FC = () => {
  const orderedConfigurations = useAppSelector(selectOrderedConfigurations)

  const comparison = useMatch('/configuration/:baseId/step/:number/compare/:comparisonId')
  const configurationId = useMatch({ path: '/configuration/:baseId', end: false })?.params.baseId

  const activeConfiguration = useMemo(() => {
    if (configurationId && isTmsConfigurationId(configurationId)) {
      const [manCode, sortOrder] = configurationId.split(':')
      const sortOrderNumber = Number.parseInt(sortOrder, 10)
      return orderedConfigurations.find(
        (x) => x.id === configurationId || (x.apiConfiguration.manCode === manCode && x.sortOrder === sortOrderNumber),
      ) as TruckConfiguration | undefined
    }
    if (configurationId && isLocalConfigurationId(configurationId)) {
      return orderedConfigurations.find((x) => x.id === configurationId) as TruckConfiguration | undefined
    }
    return undefined
  }, [configurationId, orderedConfigurations])

  return (
    <SharedHeaderTabsContainer>
      <HeaderTabs
        entries={[
          ...orderedConfigurations.map((x) => ({
            key: x.id,
            tab: <ConfigurationHeaderTab configuration={x} allConfigurations={orderedConfigurations} activeConfiguration={activeConfiguration} />,
            dropdown: x.type === 'truck-configuration' ? <ConfigurationHeaderDropdownLink configuration={x} /> : undefined,
            highlighted: comparison?.params.comparisonId === x.id,
          })),
          {
            key: 'add-config',
            tab: <ManCodeHeader />,
            dropdown: undefined,
            cannotBeSelected: true,
            alwaysVisible: true,
          },
        ]}
        selection={activeConfiguration?.id}
      />
    </SharedHeaderTabsContainer>
  )
}
